/**
 * Tutorial Action Model - Defines interactive action buttons in tutorials
 * 
 * This model enables tutorials to include call-to-action buttons that can:
 * - Navigate to other pages in the application
 * - Toggle feature flags
 * - Execute custom actions
 * - Only display to users with specific roles/permissions
 */

/**
 * Types of actions that can be triggered from a tutorial
 */
export enum TutorialActionType {
  /**
   * Navigate to a route in the application
   * Example: Navigate to /board/:id/admin/config
   */
  NAVIGATE = 'navigate',

  /**
   * Toggle a feature flag on the board config
   * Example: Toggle showDelivery flag
   */
  TOGGLE_FEATURE = 'toggle_feature',

  /**
   * Execute a custom action (extensible for future use)
   */
  CUSTOM = 'custom'
}

/**
 * User role/permission levels for tutorial actions
 */
export enum TutorialActionPermission {
  /**
   * Available to all users
   */
  PUBLIC = 'public',

  /**
   * Available only to board admins/managers
   */
  ADMIN = 'admin',

  /**
   * Available only to users with edit permissions
   */
  EDITOR = 'editor',

  /**
   * Custom permission check (handler must validate)
   */
  CUSTOM = 'custom'
}

/**
 * Navigate action configuration
 * Used when TutorialActionType is NAVIGATE
 */
export interface NavigateAction {
  type: TutorialActionType.NAVIGATE;
  label: string;
  route: string;
  queryParams?: { [key: string]: any };
}

/**
 * Toggle feature action configuration
 * Used when TutorialActionType is TOGGLE_FEATURE
 */
export interface ToggleFeatureAction {
  type: TutorialActionType.TOGGLE_FEATURE;
  label: string;
  featureFlag: string;
  successMessage?: string;
}

/**
 * Custom action configuration
 * Used when TutorialActionType is CUSTOM
 */
export interface CustomAction {
  type: TutorialActionType.CUSTOM;
  label: string;
  actionId: string; // Component must handle this ID
  payload?: any;
}

/**
 * Union type for all action configurations
 */
export type TutorialAction = NavigateAction | ToggleFeatureAction | CustomAction;

/**
 * Action button displayed in a tutorial step
 * Includes metadata about when and how to display the action
 */
export interface TutorialActionButton {
  /**
   * The action configuration (navigate, toggle, custom)
   */
  action: TutorialAction;

  /**
   * Permission level required to see this button
   */
  requiredPermission: TutorialActionPermission;

  /**
   * CSS class for button styling (primary, success, info, etc.)
   */
  buttonClass?: string;

  /**
   * Icon for the button (Font Awesome icon name)
   */
  icon?: string;

  /**
   * Whether this action is the "primary" action for the step
   */
  isPrimary?: boolean;

  /**
   * Tooltip text to display on hover
   */
  tooltip?: string;
}
